import { useRef } from 'react'
import { useSelector } from 'react-redux'
import { userSelectors } from 'store/features/user/selectors'
import { useDropdownState } from 'hooks/useDropdownState'
import { useOpenUpgradePopup } from 'hooks/useOpenUpgradePopup'
import { REF_LINKS } from 'consts/refLinks'
import AvatarActionButton from '../action-btn/AvatarActionButton'
import * as Styled from './styled'

interface IAvatarActionFlyoutButtonProps {
  icon: React.ReactElement
  tooltipText: string
  // Render-prop so the CTA inside the panel can close the flyout right before
  // it dispatches (close first, then the preview shows its loader).
  children: (close: () => void) => React.ReactNode
  testId?: string
}

// A source-image action button that opens its own flyout (desktop) / slide-up
// (mobile, via FlyoutWithMobileSlideUp). Each instance owns independent open
// state + anchor, so Resize and Enhance never interfere.
const AvatarActionFlyoutButton = ({
  icon,
  tooltipText,
  children,
  testId
}: IAvatarActionFlyoutButtonProps) => {
  const { isOpen, toggleDropdown, closeDropdown } = useDropdownState()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const isFreeUser = useSelector(userSelectors.isFreeUser)
  const openUpgradePopup = useOpenUpgradePopup({ usePlanCheck: true })

  // Paid-action gate: every flyout action (Enhance, Resize) costs credits, so
  // a free user's icon click routes straight to the upgrade popup — they never
  // see the flyout. The panel's own insufficient-credits CTA then only covers
  // subscribers whose balance ran out (refill).
  const handleClick = () => {
    if (isFreeUser) {
      openUpgradePopup({ ref: REF_LINKS.V_EDITOR_AI_AVATAR_REFILL })
      return
    }
    toggleDropdown()
  }

  return (
    <>
      <AvatarActionButton
        ref={buttonRef}
        icon={icon}
        tooltipText={tooltipText}
        isActive={isOpen}
        onClick={handleClick}
        testId={testId}
      />
      <Styled.Flyout
        isOpen={isOpen}
        onClose={closeDropdown}
        anchorEl={buttonRef.current}
        options={{ position: 'bottom', alignmentX: 'right', extraOffset: 8 }}
        // Mobile SlideUp: sit flush above the 64px bottom tab bar (Sidebar),
        // otherwise the panel's CTA is hidden behind it. Same value as
        // VolumeControlButton. Ignored on desktop (flyout path).
        bottomMargin={64}
      >
        {children(closeDropdown)}
      </Styled.Flyout>
    </>
  )
}

export default AvatarActionFlyoutButton
